import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import VConsentChecklistTasksComposite from '../../interfaces/v-consent-checklist-tasks-composite';
import { BehaviorSubject, Cons, Subscription } from 'rxjs';
import ConsentChecklistStatus from '../../interfaces/consent-checklist-status';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import {
  ConsentChecklistTaskFilterInput,
  FETCH_CONSENT_CHECKLIST_TASK_PAGE,
  FETCH_CONSENT_CHECKLIST_TASKS,
  FetchConsentChecklistTaskPageResponse,
  FetchConsentChecklistTaskPageVariables,
  QueryGetConsentChecklistTasksOrderOrderByClause,
} from './graphql/consent-checklists';
import { ConsentStatus } from '../../interfaces/consent-status';
import Permission from '../../interfaces/permission';

export const defaultOrder: QueryGetConsentChecklistTasksOrderOrderByClause[] = [
  { column: 'IS_URGENT_FLAG', order: 'ASC' },
  { column: 'TIMING_DATE', order: 'ASC' },
  { column: 'RECEIVED_DATE', order: 'ASC' },
];
@Injectable({
  providedIn: 'root',
})
export class ConsentChecklistsService {
  private _consentChecklistTasks: Paginator<
    VConsentChecklistTasksComposite,
    PaginatorInfo
  > = {
    data: [],
    paginatorInfo: {
      currentPage: 1,
      total: 0,
      hasMorePages: false,
    },
  };
  private consentChecklistTasks = new BehaviorSubject(
    this._consentChecklistTasks
  );
  public consentChecklistTasks$ = this.consentChecklistTasks.asObservable();

  private _consentChecklistStatuses: ConsentChecklistStatus[] = [];
  private consentChecklistStatuses = new BehaviorSubject(
    this._consentChecklistStatuses
  );
  public consentChecklistStatuses$ =
    this.consentChecklistStatuses.asObservable();

  private _checklistParties: string[] = [];
  private checklistParties = new BehaviorSubject(this._checklistParties);
  public checklistParties$ = this.checklistParties.asObservable();

  private _loading: boolean = false;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  private _consentStatuses: ConsentStatus[] = [];
  private consentStatuses = new BehaviorSubject(this._consentStatuses);
  public consentStatuses$ = this.consentStatuses.asObservable();

  private _permission: Permission = {
    _create: false,
    _read: false,
    _edit: false,
    _delete: false,
    authorized_fields: null,
    __typename: 'Permission',
  };
  private permission = new BehaviorSubject(this._permission);
  public permission$ = this.permission.asObservable();

  public taskSub: Subscription;
  public taskQuery: QueryRef<
    FetchConsentChecklistTaskPageResponse,
    FetchConsentChecklistTaskPageVariables
  >;
  constructor(
    private apollo: Apollo,
    private _snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  private setChecklistParties(checklistParties: string[]) {
    this._checklistParties = checklistParties;
    this.checklistParties.next(this._checklistParties);
  }
  private setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.next(this._loading);
  }
  private setConsentChecklistStatuses(
    consentChecklistStatuses: ConsentChecklistStatus[]
  ) {
    this._consentChecklistStatuses = consentChecklistStatuses;
    this.consentChecklistStatuses.next(this._consentChecklistStatuses);
  }
  private setConsentChecklistTasks(
    consentChecklistTasks: Paginator<
      VConsentChecklistTasksComposite,
      PaginatorInfo
    >
  ) {
    this._consentChecklistTasks = consentChecklistTasks;
    this.consentChecklistTasks.next(this._consentChecklistTasks);
  }
  private setConsentStatuses(consentStatuses: ConsentStatus[]) {
    this._consentStatuses = consentStatuses;
    this.consentStatuses.next(this._consentStatuses);
  }
  private setPermission(permission?: Permission) {
    if (permission) {
      this._permission = permission;
      this.permission.next(this._permission);
    }
  }
  public getConsentChecklistTaskView() {
    this.setLoading(true);
    this.apollo
      .query({
        query: FETCH_CONSENT_CHECKLIST_TASKS,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            task_complete: false,
            developer_id: null
          },
          excludeRequestStatuses: ['Complete', 'Denied', 'GP Withdrew'],
          order: defaultOrder,
        },
      })
      .subscribe(({ data }) => {
        this.setLoading(false);
        if (!data) return;
        this.setChecklistParties(data.getChecklistParties as string[]);
        this.setConsentChecklistStatuses(data.getConsentChecklistStatuses);
        this.setConsentChecklistTasks(
          data.getConsentChecklistTasks as Paginator<
            VConsentChecklistTasksComposite,
            PaginatorInfo
          >
        );
        this.setConsentStatuses(data.getConsentStatuses);
        this.setPermission(data.me.permissionsForResource);
      });
  }
  public getConsentChecklistTaskPage(
    page: number,
    filter?: ConsentChecklistTaskFilterInput,
    order?: QueryGetConsentChecklistTasksOrderOrderByClause[]
  ) {
    if (!filter) {
      filter = {};
    }
    filter.developer_id = null;
    if (!filter?.checklist_status_id && !filter?.project_id) {
      filter.task_complete = false;
    }
    let variables: FetchConsentChecklistTaskPageVariables = {
      page,
      order: order ?? defaultOrder,
      filter,
      excludeRequestStatuses:
        filter?.request_status_id || filter?.project_id
          ? null
          : ['Complete', 'Denied', 'GP Withdrew'],
    };
    this.setLoading(true);
    this.taskQuery = this.apollo.watchQuery({
      query: FETCH_CONSENT_CHECKLIST_TASK_PAGE,
      variables,
      fetchPolicy: 'network-only',
    });
    this.taskSub = this.taskQuery.valueChanges.subscribe(({ data }) => {
      this.setLoading(false);
      if (!data) return;
      this.setConsentChecklistTasks(
        data.getConsentChecklistTasks as Paginator<
          VConsentChecklistTasksComposite,
          PaginatorInfo
        >
      );
      this.setChecklistParties(data.getChecklistParties as string[]);
    });
  }

  public tearDown() {
    this.taskSub?.unsubscribe();
  }
}
